import { useRef, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { api, API } from "../../lib/api";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * resolveImageUrl: convert backend-relative /api/files/... paths into absolute URLs
 * so <img src> works regardless of where the frontend is hosted.
 */
export const resolveImageUrl = (value) => {
  if (!value) return "";
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  if (value.startsWith("/api/")) return `${BACKEND_URL}${value}`;
  return value;
};

export default function ImageUpload({ value, onChange, label = "Image", testid = "image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image exceeds the 5MB limit.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Image uploaded.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const preview = resolveImageUrl(value);

  return (
    <div data-testid={testid}>
      <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">{label}</label>
      <div className="mt-1.5 rounded-md border border-slate-200 p-3 flex items-start gap-4">
        <div className="h-24 w-24 rounded-md bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center text-slate-400 text-xs">
          {preview ? (
            <img src={preview} alt="preview" className="h-full w-full object-cover" />
          ) : (
            "No image"
          )}
        </div>
        <div className="flex-1 min-w-0">
          <input
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Paste an image URL or upload below"
            className="w-full px-3 py-2 rounded-md border border-slate-200 focus:border-[#FF8C00] outline-none text-[#0A1128] text-sm"
          />
          <div className="mt-2 flex items-center gap-2">
            <input ref={inputRef} type="file" accept="image/*" hidden onChange={handle} data-testid={`${testid}-input`} />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`${testid}-upload-btn`}
              className="inline-flex items-center gap-2 rounded-md bg-[#0A1128] hover:bg-[#15244B] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wide disabled:opacity-60"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? "Uploading..." : "Upload image"}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 hover:border-red-500 hover:text-red-500 px-3 py-2 text-xs font-semibold"
              >
                <X size={14} /> Clear
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
